// src/lib/api.js
// Vessel data: Supabase tables read and written as the user (row rules apply), mapped to the display labels below.
// Other calls go through our own /api routes (the n8n webhook URL never touches the browser).

import { supabase, getClientConfig } from './supabase'
import { ARCHIVABLE, missingArchive } from './archive'
import { canSeeOthers, redactCrew, visibleDocs, splitCrewRow, crewFullSelect, crewSelect } from './crewPrivacy'

// Get the current user's JWT to authenticate with our backend
async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')
  return { 'Authorization': `Bearer ${session.access_token}` }
}

async function apiCall(path, options = {}, _retries = 2) {
  const authHeader = await getAuthHeader()
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...(options.headers || {}),
    },
  })

  if (res.status === 204) return null // DELETE success

  const data = await res.json().catch(() => ({}))

  if (!res.ok) {
    // Auto-retry on Baserow throttling (429 / "throttled")
    const throttled = res.status === 429 ||
      (typeof data.detail === 'string' && data.detail.toLowerCase().includes('throttl')) ||
      (typeof data.error === 'string' && data.error.toLowerCase().includes('throttl'))
    if (throttled && _retries > 0) {
      await new Promise(r => setTimeout(r, 1500))
      return apiCall(path, options, _retries - 1)
    }
    throw new Error(data.error || data.detail || `Server error ${res.status}`)
  }

  return data
}

// ─── SUPABASE-NATIVE TABLES (migrated off Baserow) ────────────────────────────
// To move a table to Supabase: add it here with its column<->displayKey map.
// To roll a table back to Baserow: remove its entry. DataTable/tableConfig unchanged.
const SUPA_TABLES = {
  hours_rest: {
    table: 'hours_rest',
    orderBy: 'sort_order',
    cols: { crew_member: 'Crew Member', crew_id: 'Crew ID', position: 'Position', month: 'Month', vessel: 'Vessel', days: 'Days', status: 'Status', signed_by_master: 'Signed by Master', signed_by_crew: 'Signed by Crew', onboard: 'Onboard', notes: 'Notes', report_url: 'Report URL' },
    nums: [],
    bools: ['onboard'],
  },
  day_log: {
    table: 'day_log',
    orderBy: 'sort_order',
    cols: { entry: 'Entry', date: 'Date', time: 'Time', watch_keeper: 'Watch Keeper', status: 'Status', position: 'Position', wind_direction: 'Wind Direction', wind_force: 'Wind Force', visibility: 'Visibility', cloud_cover: 'Cloud Cover', pressure: 'Pressure', air_temp: 'Air Temp', water_temp: 'Water Temp', significant_events: 'Significant Events' },
    nums: [],
    bools: [],
  },
  engine_log: {
    table: 'engine_log',
    orderBy: 'sort_order',
    cols: { entry: 'Entry', date: 'Date', location: 'Location', mode: 'Mode', signed_by: 'Signed By', fuel_total: 'Fuel Total', day_tank_number: 'Day Tank Number', fresh_water_number: 'Fresh Water Number', clean_lo_number: 'Clean LO Number', waste_lo_number: 'Waste LO Number', me_port_hours: 'ME Port Hours', me_stbd_hours: 'ME Stbd Hours', gen_port_hours: 'Gen Port Hours', gen_stbd_hours: 'Gen Stbd Hours', bilges_dry: 'Bilges Dry', readings: 'Readings', notes: 'Notes' },
    nums: [],
    bools: [],
  },
  running_log: {
    table: 'running_log',
    orderBy: 'sort_order',
    cols: { entry: 'Entry', date: 'Date', departure_time: 'Departure Time', arrival_time: 'Arrival Time', voyage_origin: 'Voyage Origin', voyage_destination: 'Voyage Destination', departure_fuel: 'Departure Fuel', arrival_fuel: 'Arrival Fuel', fuel_consumption: 'Fuel Consumption', number_of_crew: 'Number of Crew', number_of_guests: 'Number of Guests', hours: 'Hours', notes: 'Notes' },
    nums: [],
    bools: [],
  },
  vessel_particulars: {
    table: 'vessel_particulars',
    orderBy: 'sort_order',
    cols: { imo: 'IMO', mmsi: 'MMSI', call_sign: 'Call Sign', flag: 'Flag', registration: 'Registration', home_port: 'Home Port', loa: 'LOA', beam: 'Beam', draft: 'Draft', gross_tonnage: 'Gross Tonnage', net_tonnage: 'Net Tonnage', propulsion: 'Propulsion', owner: 'Owner', email: 'Email', year_built: 'Year Built', builder: 'Builder', max_speed: 'Max Speed', cruise_speed: 'Cruise Speed', max_draft: 'Max Draft', max_air_draft: 'Max Air Draft', class: 'Class', hull: 'Hull', displacement: 'Displacement', engines: 'Engines', total_kw: 'Total KW', billing_name: 'Billing Name', billing_address: 'Billing Address', vat_number: 'VAT Number' },
    nums: [],
    bools: [],
  },
  marina_bookings: {
    table: 'marina_bookings',
    orderBy: 'arrival_date',
    cols: {
      marina_name: 'Marina Name', location: 'Location', status: 'Status',
      arrival_date: 'Arrival Date', departure_date: 'Departure Date', berth_number: 'Berth Number',
      loa_booked: 'LOA Booked', rate: 'Rate', currency: 'Currency', deposit_status: 'Deposit Status',
      booking_ref: 'Booking Ref', contact: 'Contact', notes: 'Notes', vessel: 'Vessel',
    },
    nums: ['loa_booked', 'rate'],
    bools: [],
  },
  charters: {
    table: 'charters',
    orderBy: 'start_date',
    cols: {
      charter_ref: 'Charter Ref', status: 'Status', charter_type: 'Charter Type',
      start_date: 'Start Date', end_date: 'End Date', embark_port: 'Embark Port', disembark_port: 'Disembark Port',
      itinerary: 'Itinerary', broker: 'Broker', broker_contact: 'Broker Contact', lead_guest: 'Lead Guest',
      number_of_guests: 'Number of Guests', special_requests: 'Special Requests', charter_rate: 'Charter Rate',
      apa: 'APA', currency: 'Currency', deposit_status: 'Deposit Status', notes: 'Notes', vessel: 'Vessel',
    },
    nums: ['number_of_guests', 'charter_rate', 'apa'],
    bools: [],
  },
  port_calls: {
    table: 'port_calls',
    orderBy: 'eta_date',
    cols: {
      port_call_ref: 'Port Call', port: 'Port', country: 'Country', status: 'Status',
      eta_date: 'ETA Date', eta_time: 'ETA Time', etd_date: 'ETD Date', etd_time: 'ETD Time',
      berth: 'Berth', agent: 'Agent', agent_contact: 'Agent Contact', clearance_status: 'Clearance Status',
      pilot_required: 'Pilot Required', services_requested: 'Services Requested', notes: 'Notes', vessel: 'Vessel',
    },
    nums: [],
    bools: [],
  },
  calendar: {
    table: 'calendar_events',
    orderBy: 'start_date',
    cols: {
      title: 'Title', type: 'Type', start_date: 'Start Date', end_date: 'End Date',
      start_time: 'Start Time', end_time: 'End Time', all_day: 'All Day', vessel: 'Vessel',
      notes: 'Notes', reference_id: 'Reference ID', created_by: 'Created By',
    },
    nums: [],
    bools: ['all_day'],
  },
  announcements: {
    table: 'announcements',
    orderBy: 'posted_date',
    cols: {
      title: 'Title', body: 'Body', active: 'Active', category: 'Category', priority: 'Priority',
      posted_by: 'Posted by', posted_date: 'Posted date', attachments: 'Attachments',
      source_url: 'Source URL', compliance_pack: 'Compliance Pack',
    },
    nums: [],
    bools: ['active'],
  },
  assets: {
    table: 'assets',
    orderBy: 'position',
    cols: {
      name: 'Name', type: 'Type', make: 'Make', model: 'Model',
      serial: 'Serial', notes: 'Notes', position: 'Position',
    },
    nums: ['position'],
    bools: [],
  },
  news_archive: {
    table: 'news_archive',
    orderBy: 'published',
    cols: {
      title: 'title', source: 'source', link: 'link',
      published: 'published', tier: 'tier', summary: 'summary', archived_on: 'archived_on',
    },
    nums: [],
    bools: [],
  },
  equipment: {
    table: 'equipment',
    orderBy: 'uid',
    cols: {
      uid: 'UID', name: 'Name', make: 'Make', model: 'Model', serial: 'Serial',
      department: 'Department', location: 'Location', status: 'Status',
      current_hours: 'Current Hours', system: 'System', comments: 'Comments',
      supplier: 'Supplier', critical_flag: 'Critical Flag', out_of_service_since: 'Out of Service Since',
      status_reason: 'Status Reason', status_by: 'Status By',   // sent with a status change only (the database clears them)
      archived_at: 'Archived At', archived_by: 'Archived By', archive_reason: 'Archive Reason',   // sql/archive.sql: null = active
    },
    nums: ['current_hours'],
    bools: ['critical_flag'],
  },
  mtask: {
    table: 'maintenance_tasks',
    equipmentName: 'Equipment',
    orderBy: 'plan_id',
    cols: {
      plan_id: 'Plan ID', equipment_uid: 'Equipment', task_name: 'Task Name',
      frequency_hours: 'Frequency Hours', frequency_days: 'Frequency Days',
      required_kit: 'Required Kit', last_service_date: 'Last Service Date',
      last_service_hours: 'Last Service Hours', next_service_date: 'Next Service Date',
      next_service_hours: 'Next Service Hours', status: 'Status',
      archived_at: 'Archived At', archived_by: 'Archived By', archive_reason: 'Archive Reason',
    },
    nums: ['frequency_hours', 'frequency_days', 'last_service_hours', 'next_service_hours'],
    bools: [],
  },
  mlog: {
    table: 'maintenance_log',
    orderBy: 'log_id',
    cols: {
      log_id: 'Log ID', equipment_uid: 'Component', date_completed: 'Date Completed',
      task: 'Task', work_description: 'Work Description', hours_at_service: 'Hours at Service',
      parts_used: 'Parts Used', quantity: 'Quantity', technician: 'Technician',
      cost: 'Cost', comments: 'Comments',
    },
    nums: ['hours_at_service', 'quantity', 'cost'],
    bools: [],
  },
  inventory: {
    table: 'inventory',
    orderBy: 'part_uid',
    cols: {
      part_uid: 'Part UID', part_name: 'Part Name', category: 'Category', make: 'Make',
      part_number: 'Part Number', quantity: 'Quantity', min_stock: 'Min Stock',
      location: 'Location', equipment_uid: 'Equipment', critical_flag: 'Critical Flag',
      status: 'Status', department: 'Department', critical_spare: 'Critical Spare', preferred_supplier_id: 'Preferred Supplier',
    },
    nums: ['quantity', 'min_stock'],
    bools: ['critical_flag', 'critical_spare'],
  },
  docs: {
    table: 'documents',
    orderBy: 'created_at',
    cols: {
      title: 'Title', document_type: 'Document Type', doc_id: 'Doc ID',
      folder: 'Folder', department: 'Department',
      issued_date: 'Issued Date', expiry_date: 'Expiry Date',
      survey_due: 'Survey Due', file_url: 'File URL',
      status: 'Status', notes: 'Notes', asset_id: 'asset_id', attachments: 'Attachments',
    },
    nums: [],
    bools: [],
  },
  crew: {
    table: 'crew',
    // personal columns live in crew_private (sql/test-fixes-5.sql): read through the crew_full view, written to crew_private
    readFrom: 'crew_full',
    privateTable: 'crew_private',
    orderBy: 'crew_id',
    cols: {
      crew_id: 'Crew ID', first_name: 'First Name', last_name: 'Last Name',
      name: 'Name', position: 'Position', department: 'Department', status: 'Status',
      nationality: 'Nationality', phone: 'Phone', email: 'Email',
      date_of_birth: 'Date of Birth', place_of_birth: 'Place of Birth',
      passport_no: 'Passport No', passport_issue_place: 'Passport Issue Place',
      passport_issued_date: 'Passport Issued Date', passport_expiry_date: 'Passport Expiry Date',
      seamans_book_no: "Seaman's Book No", seamans_book_expiry_date: "Seaman's Book Expiry Date",
      contract_start_date: 'Contract Start Date', contract_end_date: 'Contract End Date',
      leave_entitlement_days: 'Leave Entitlement Days',
      onboard: 'Onboard', archived: 'Archived', emergency_contact_name: 'Emergency Contact Name',
      emergency_contact_phone: 'Emergency Contact Phone', photo_url: 'Photo URL', notes: 'Notes',
    },
    nums: [],
    bools: ['onboard'],
  },
  crew_log: {
    table: 'crew_log',
    orderBy: 'date_from',
    cols: {
      log_entry: 'Log Entry', crew_member: 'Crew Member', crew_id: 'Crew ID',
      action: 'Action', date_from: 'Date From', date_to: 'Date To',
      employment_status: 'Employment Status', port: 'Port', notes: 'Notes',
    },
    nums: [],
    bools: [],
  },
  crew_docs: {
    table: 'crew_docs',
    orderBy: 'expiry_date',
    cols: {
      title: 'Title', type: 'Type', doc_id: 'Doc ID',
      issued_date: 'Issued Date', expiry_date: 'Expiry Date',
      status: 'Status', file_url: 'File URL', notes: 'Notes',
      crew_id: 'Crew Record',   // the crew member (crew.id) - their own documents are found by it (sql/test-fixes-5.sql)
    },
    nums: [],
    bools: [],
  },
  crew_list: {
    table: 'crew_list',
    orderBy: 'date',
    cols: {
      name: 'Name', active: 'Active', title: 'Title', date: 'Date',
      signatory: 'Signatory', filter: 'Filter', file_url: 'File URL', notes: 'Notes',
    },
    nums: [],
    bools: ['active'],
  },
  crew_leave: {
    table: 'crew_leave',
    orderBy: 'start_date',
    cols: {
      crew_id: 'Crew ID', crew_name: 'Crew Name', leave_type: 'Leave Type',
      start_date: 'Start Date', end_date: 'End Date', days: 'Days', notes: 'Notes',
    },
    nums: ['days'],
    bools: [],
  },
  diary: {
    table: 'diary_entries',
    orderBy: 'entry_date',
    cols: {
      entry_date: 'Entry Date', title: 'Title', body: 'Body',
      tags: 'Tags', location: 'Location', created_by: 'Created By',
    },
    nums: [],
    bools: [],
  },
}

function supaFromDb(row, map) {
  const out = { id: row.id, created_at: row.created_at }
  for (const [col, disp] of Object.entries(map.cols)) out[disp] = row[col]
  return out
}
function supaToDb(body, map) {
  const rev = {}
  for (const [col, disp] of Object.entries(map.cols)) rev[disp] = col
  const db = {}
  for (const [k, v] of Object.entries(body)) {
    const col = rev[k]
    if (!col) continue
    if (map.nums.includes(col)) db[col] = (v === '' || v === null || v === undefined) ? null : Number(v)
    else if (map.bools.includes(col)) db[col] = !!v
    else if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) db[col] = v.value
    else db[col] = (v === '' ? null : v)
  }
  return db
}

// The vessel whose data fetchRows should return. Normally the logged-in vessel;
// for a company drilling into a vessel, set to that vessel's id so queries scope to it
// (company RLS allows the whole fleet, so without this filter fetchRows would merge all vessels).
let _activeClientId = null
export function setActiveClientId(id) { _activeClientId = id || null }
// crew personal data (src/lib/crewPrivacy.js): set from the login; every page reading crew / crew documents gets the same rule
let _viewer = null
export function setViewer(cfg) { _viewer = cfg ? { accountType: cfg.accountType, crewId: cfg.crewId || null, permissions: cfg.permissions || {} } : null }
export function privacyRows(table, rows, viewer = _viewer) {
  if (!viewer || viewer.accountType !== 'crew' || canSeeOthers('crew', viewer.permissions)) return rows
  if (table === 'crew' || table === 'crew_full') return rows.map(r => redactCrew(r, viewer.crewId, false))
  if (table === 'crew_docs') return visibleDocs(rows, viewer.crewId, false)
  if (table === 'crew_list') return rows.map(({ file_url, ...r }) => r)
  return rows
}

// the view / table is not there yet (sql/test-fixes-5.sql not run): read / write the old way
const missingRel = e => !!e && /PGRST205|42P01|does not exist|Could not find the table|schema cache/i.test((e.code || '') + ' ' + (e.message || ''))
// crew: never select=* (refused since sql/test-fixes-5-lock.sql) - crew_full and crew with named columns only
const selectFor = (map, from, cols) => (map.table !== 'crew' ? cols : from === 'crew' ? crewSelect(cols) : crewFullSelect(cols))
// crew_full could not be read: the list still loads from crew (no personal columns) - the real error goes to the log, not lost
function readFromFailed(map, error) {
  const msg = map.readFrom + ' read failed, ' + map.table + ' read without the personal columns: ' + (error.code ? error.code + ' ' : '') + (error.message || '')
  console.warn(msg)
  try { if (typeof window !== 'undefined' && window.hcReportError) window.hcReportError(new Error(msg), 'crew_full') } catch (e) {}
}
// archived equipment / tasks (sql/archive.sql, src/lib/archive.js): lists skip them unless asked; no column yet -> no filter
let _archiveMissing = false
async function supaFetch(map, explicitCid, opts = {}) {
  let cid = explicitCid || _activeClientId
  if (!cid) { try { const cfg = await getClientConfig(); cid = cfg?.id } catch {} }
  const arch = ARCHIVABLE.includes(map.table) && !_archiveMissing ? (opts.archived || 'active') : 'all'
  const read = (from, a = arch) => {
    let q = supabase.from(from).select(selectFor(map, from, '*')).order(map.orderBy, { ascending: true }); if (cid) q = q.eq('client_id', cid)
    if (a === 'active') q = q.is('archived_at', null); else if (a === 'only') q = q.not('archived_at', 'is', null)
    return q
  }
  let { data, error } = await read(map.readFrom || map.table)
  if (error && arch !== 'all' && missingArchive(error.message)) { _archiveMissing = true; if (arch === 'only') return []; ({ data, error } = await read(map.readFrom || map.table, 'all')) }
  if (error && map.readFrom) { readFromFailed(map, error); ({ data, error } = await read(map.table)) }
  if (error) throw new Error(error.message)
  const rows = privacyRows(map.table, data || []).map(r => supaFromDb(r, map))
  // show the equipment NAME next to its code (e.g. "Generator Port (EQ-068)")
  if (map.equipmentName && cid) {
    try {
      const { data: eq } = await supabase.from('equipment').select('uid,name').eq('client_id', cid)
      const nm = Object.fromEntries((eq || []).map(e => [e.uid, e.name]))
      rows.forEach(r => { const u = r[map.equipmentName]; r['Equipment Name'] = u ? (nm[u] ? nm[u] + ' (' + u + ')' : u) : '' })
    } catch (e) {}
  }
  // maintenance tasks: ONE status for pills and filter chips - the more urgent of date and hours
  if (map.table === 'maintenance_tasks') {
    const RANK = { Overdue: 3, Due: 2, Upcoming: 1 }
    const today = new Date(); today.setHours(0, 0, 0, 0)
    rows.forEach(r => {
      const stored = r['Status'] === 'OK' || !r['Status'] ? 'Upcoming' : r['Status']
      if (stored === 'Completed') return
      let byDate = null
      if (r['Next Service Date']) {
        const d = new Date(r['Next Service Date']); const diff = (d - today) / 86400000
        if (!isNaN(diff)) byDate = diff < 0 ? 'Overdue' : diff <= 30 ? 'Due' : 'Upcoming'
      }
      const byHours = r['Next Service Hours'] != null && r['Next Service Hours'] !== '' ? stored : null
      const cands = [byDate, byHours].filter(Boolean)
      r['Status'] = cands.length ? cands.sort((x, y) => (RANK[y] || 0) - (RANK[x] || 0))[0] : stored
    })
  }
  return rows
}
async function supaCreate(map, body) {
  const cfg = await getClientConfig()
  const db = { ...supaToDb(body, map), client_id: cfg?.id }
  if (map.privateTable) {
    // crew: the record in crew, the personal data in crew_private (logins may not write those columns of crew any more)
    const { base, priv } = splitCrewRow(db)
    const { data, error } = await supabase.from(map.table).insert(base).select('id').single()
    if (error) throw new Error(error.message)
    await savePrivate(map, data.id, base.client_id, priv)
    return fetchOne(map, data.id)
  }
  const { data, error } = await supabase.from(map.table).insert(db).select().single()
  if (error) throw new Error(error.message)
  return supaFromDb(data, map)
}
async function supaUpdate(map, rowId, body) {
  if (map.privateTable) {
    const { base, priv } = splitCrewRow(supaToDb(body, map))
    if (Object.keys(base).length) { const { error } = await supabase.from(map.table).update(base).eq('id', rowId); if (error) throw new Error(error.message) }
    await savePrivate(map, rowId, _activeClientId || (await getClientConfig())?.id, priv)
    return fetchOne(map, rowId)
  }
  // nothing changed (or only shown-only fields like "Equipment Name"): no PATCH - an empty one returns no row and
  // .single() said "Cannot coerce the result to a single JSON object" (Inventory saved with no change)
  const patch = supaToDb(body, map)
  if (!Object.keys(patch).length) return fetchOne(map, rowId)
  const { data, error } = await supabase.from(map.table).update(patch).eq('id', rowId).select().maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) throw new Error('nothing was changed - this record may have been deleted, or this login may not edit it.')
  return supaFromDb(data, map)
}
// personal data of one crew member -> crew_private (nothing asked back: a login may write what it may not read)
async function savePrivate(map, id, clientId, priv) {
  if (!Object.keys(priv).length) return
  const { error } = await supabase.from(map.privateTable).upsert({ crew_id: id, client_id: clientId, ...priv }, { onConflict: 'crew_id' })
  if (!error) return
  if (!missingRel(error)) throw new Error(error.message)
  const { error: e2 } = await supabase.from(map.table).update(priv).eq('id', id)   // before sql/test-fixes-5.sql: the old columns
  if (e2) throw new Error(e2.message)
}
// crew rows with the database column names (personal columns from crew_private where this login may read them)
export async function fetchCrewDb(clientId, cols = '*') {
  const map = SUPA_TABLES.crew
  const read = from => supabase.from(from).select(selectFor(map, from, cols)).eq('client_id', clientId)
  let { data, error } = await read('crew_full')
  if (error) { readFromFailed(map, error); ({ data, error } = await read('crew')) }
  if (error) throw new Error(error.message)
  return privacyRows('crew', data || [])
}
async function fetchOne(map, id) {
  const read = from => supabase.from(from).select(selectFor(map, from, '*')).eq('id', id).single()
  let { data, error } = await read(map.readFrom || map.table)
  if (error && map.readFrom) { readFromFailed(map, error); ({ data, error } = await read(map.table)) }
  if (error) throw new Error(error.message)
  return supaFromDb(privacyRows(map.table, [data])[0], map)
}
async function supaDelete(map, rowId) {
  const { error } = await supabase.from(map.table).delete().eq('id', rowId)
  if (error) throw new Error(error.message)
  return null
}

// ─── ROWS ─────────────────────────────────────────────────────────────────────

// every table lives in Supabase (SUPA_TABLES); an unknown key is a bug, not a reason to call the retired Baserow proxy
// database table behind a page key (mtask -> maintenance_tasks), for the History link
export const dbTableOf = key => (SUPA_TABLES[key] && SUPA_TABLES[key].table) || null
const supaMap = table => { const m = SUPA_TABLES[table]; if (!m) throw new Error('Unknown table "' + table + '" (not in SUPA_TABLES)'); return m }
export async function fetchRows(table, explicitCid, opts) { return supaFetch(supaMap(table), explicitCid, opts) }
// Equipment / Maintenance Tasks "Show archived": the archived rows (none before sql/archive.sql)
export async function fetchArchived(table, explicitCid) { return supaFetch(supaMap(table), explicitCid, { archived: 'only' }) }
export const archiveReady = () => !_archiveMissing
// a direct supabase read of equipment / maintenance_tasks: active rows only (make() builds a fresh query each time)
export async function readActive(table, make) {
  if (!_archiveMissing && ARCHIVABLE.includes(table)) {
    const r = await make().is('archived_at', null)
    if (!r.error || !missingArchive(r.error.message)) return r
    _archiveMissing = true
  }
  return make()
}
// Archive / Unarchive one row (who archived it is stamped by the database); archiving equipment archives its tasks with it
export async function archiveRow(table, rowId, reason) { return supaUpdate(supaMap(table), rowId, { 'Archived At': new Date().toISOString(), 'Archive Reason': reason || '' }) }
export async function unarchiveRow(table, rowId) { return supaUpdate(supaMap(table), rowId, { 'Archived At': null, 'Archived By': null, 'Archive Reason': null }) }
export async function createRow(table, body) { return supaCreate(supaMap(table), body) }
export async function updateRow(table, rowId, body) { return supaUpdate(supaMap(table), rowId, body) }
export async function deleteRow(table, rowId) { return supaDelete(supaMap(table), rowId) }

// ─── CHAT ─────────────────────────────────────────────────────────────────────

export async function sendChatMessage(message) {
  const data = await apiCall('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ message }),
  })
  return data.reply
}

// ─── ADMIN ────────────────────────────────────────────────────────────────────

async function adminCall(method, body) {
  const adminKey = sessionStorage.getItem('hc_admin_key')
  if (!adminKey) throw new Error('Not authenticated as admin')

  const res = await fetch('/api/admin', {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  const data = await res.json()
  if (!res.ok) throw new Error(data.error || `Admin error ${res.status}`)
  return data
}

export async function adminListClients() {
  return adminCall('GET')
}

export async function adminCreateClient(clientData) {
  return adminCall('POST', { action: 'create_client', ...clientData })
}

export async function adminDeleteClient(clientId, userId) {
  return adminCall('DELETE', { action: 'delete_client', client_id: clientId, user_id: userId })
}
